import React, { useEffect, useState } from "react";
import LoadingWidget from "../../../../context_providers/modal/loader";
import Icon from "@mdi/react";
import { mdiAlertOutline, mdiLockOpenOutline } from "@mdi/js";
import {
  StockTransferSubtype,
  WarehouseOutwardModel,
  WarehouseOutwardStatus,
} from "../model/warehouse_outward_model";
import "../inward-styles.css";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import DeviceIdsViewer from "../common-components/DeviceIdsViewer";
import { DeleteRejectedeOutwardApi } from "../warehouse_module_repo";

type DeleteRejectedSTModalProps = {
  open: boolean;
  onClose: () => void;
  selectedOutward?: WarehouseOutwardModel | null;
};

const DeleteRejectedSTModal: React.FC<DeleteRejectedSTModalProps> = ({
  open,
  onClose,
  selectedOutward,
}) => {
  const [loading, setLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (open) {
      setConfirmCancel(false);
    }
  }, [open]);

  if (!open || !selectedOutward) return null;
  const isRegularRejected =
    selectedOutward.type === "ST" &&
    selectedOutward.st_subtype === StockTransferSubtype.REGULAR &&
    selectedOutward.status === WarehouseOutwardStatus.REJECTED;

  if (!isRegularRejected) return null;

  console.log("selected  outward : ", selectedOutward);
  /* ---------------- Allocation Summary ---------------- */
  const allocationSummary = selectedOutward.items?.reduce(
    (acc, item) => {
      if (item.subCategory === "Finished Goods") {
        acc.requested += Number(item.requestedQty) || 0;
        acc.allocated += Array.isArray(item.deviceIds)
          ? item.deviceIds.length
          : 0;
      }
      return acc;
    },
    { requested: 0, allocated: 0 },
  ) ?? { requested: 0, allocated: 0 };

  const allDeviceIds: string[] =
    selectedOutward.items?.reduce((acc: string[], item) => {
      if (
        item.subCategory === "Finished Goods" &&
        Array.isArray(item.deviceIds)
      ) {
        acc.push(...item.deviceIds);
      }
      return acc;
    }, []) ?? [];

  const isRejectDisabled = loading || !confirmCancel;

  /* ---------------- Cancel ---------------- */
  const handleCancelST = () => {
    if (loading) return;
    onClose();
  };

  /* ---------------- Reject API ---------------- */
  const handleRejectST = async () => {
    try {
      setLoading(true);

      const res = await DeleteRejectedeOutwardApi(selectedOutward.id);

      if (!res.success) {
        showToast({ type: ToastType.error, text: res.error });
        return;
      }

      showToast({ type: ToastType.success, text: "Deleted ST Successfully" });
      onClose();
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err?.message || "Something went wrong while rejecting the order.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-40 overflow-y-auto p-4 sm:p-8">
      <div className="relative w-full sm:max-w-2xl max-h-[85vh] bg-white shadow-lg flex flex-col rounded-lg wh-modal-xs">
        {/* Header - Fixed */}
        <div className="border-b px-6 py-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-red-600 ">
            <Icon path={mdiAlertOutline} size={1} className="text-red-600" />
            Cancel Rejected Regular ST
          </h2>
          <p id="radix-r10h" className="text-sm text-muted-foreground">
            You are about to permanently close{" "}
            <span className="font-semibold text-foreground">
              {selectedOutward.id}
            </span>
          </p>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {/* Sale Order Lock warning - Device RELEASED */}

          <div className="flex items-start gap-3 rounded-lg border warning-card-green p-3">
            <Icon path={mdiLockOpenOutline} size={1} />

            <div className="text-sm flex flex-col gap-2">
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-300">
                  {allocationSummary.allocated} Device ID(s) will be released
                </p>
                <p className="mt-1 text-amber-700 dark:text-amber-400">
                  These device IDs will become available for allocation to other
                  orders.
                </p>
              </div>
              <DeviceIdsViewer deviceIds={allDeviceIds} title={""} />
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <p className="text-base font-semibold">
              Rejection Reason:{" "}
              <span className=" font-normal">
                {selectedOutward.rejection_reason}
              </span>
            </p>
          </div>

          <div className="flex items-start gap-3 rounded-lg border warning-card-yellow p-3">
            <Icon path={mdiAlertOutline} size={1} className="text-amber-600" />

            <p className="mt-1">This action cannot be undone</p>
          </div>

          {/* Confirmation checkbox */}
          <div className="flex items-start gap-2 py-2">
            <input
              type="checkbox"
              id="warehouse-outward-delete-rejected-confirm-checkbox"
              checked={confirmCancel}
              onChange={(e) => setConfirmCancel(e.target.checked)}
              className="
                mt-0.5
                h-4 w-4
                rounded
                border border-primary
                text-primary
                focus:ring-2
                focus:ring-ring
                cursor-pointer
              "
            />

            <label
              htmlFor="confirm-cancel"
              className="cursor-pointer text-sm text-muted-foreground leading-tight"
            >
              I understand this ST will be permanently closed and the device IDs
              will be released for reuse.
            </label>
          </div>
        </div>

        <div className="border-t px-6 py-4 flex justify-end gap-2">
          <button
            onClick={handleCancelST}
            disabled={loading}
            className="h-10 rounded-md border px-4 py-2 text-sm font-semibold"
          >
            Keep ST
          </button>

          <button
            onClick={handleRejectST}
            disabled={isRejectDisabled}
            className={`h-10 rounded-md px-4 py-2 text-sm font-semibold text-white transition-all flex items-center justify-center min-w-[150px] ${
              isRejectDisabled
                ? "bg-red-300 cursor-not-allowed opacity-50"
                : "bg-red-600 hover:bg-red-700"
            }`}
          >
            {loading ? "Closing..." : "Close ST & Release IDs"}
          </button>
        </div>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
};

export default DeleteRejectedSTModal;
